
<script setup lang="ts">
import { computed, onMounted, PropType, ref, toRefs } from 'vue'

import { type Settings } from '@source-digital/sd-types'
import { Platform } from '@source-digital/experience-utils'
import { useExperiencePlayer } from '@sourcesync/experience-player-comp-vue-core'
import { usePlyrVideoControls } from '../composables/use-plyr-control'
import '../style/app.styl'
import useHlsSource from '../composables/use-hls'
import { useRangeTouch } from '../composables/use-rangetouch'
import Plyr from 'plyr'
import VuePlyr from './VuePlyr'

const props = defineProps({
  mediaUri: {
    type: String,
    required: false,
    default: undefined
  },
  settings: {
    type: Object as PropType<Settings | undefined>,
    required: false,
    default: undefined
  },
  poster: {
    type: String,
    required: false,
    default: undefined
  }
})

const emit = defineEmits(['plyr-created'])

const { settings, mediaUri } = toRefs(props)
const vuePlyrRef = ref<InstanceType<typeof VuePlyr>>()
const {
  mediaSource,
  autoplay,
  muted,
  loop,
  $behavior
} = useExperiencePlayer({ settings, mediaUri })
const { controls } = usePlyrVideoControls($behavior)
const htmlVideoEl = ref<HTMLVideoElement | null>(null)

const plyrClasses = computed(() => ({
  'plyr--init-hide-controls': $behavior.exists('hidePlayerControls')
}))

onMounted(async () => {
  if (mediaSource.value?.provider === 'html5' && mediaSource.value.ext === 'm3u8') {
    const { init: initHls } = useHlsSource(mediaSource as any, htmlVideoEl)
    await initHls()
  }
})

const { init: initRangeTouch } = useRangeTouch(['.sd-ee-plyr-template input[type="range"]'])

function getPlyr () {
  return vuePlyrRef.value?.getInstance()
}

function getPlyrElement () {
  return vuePlyrRef.value?.getElement()
}

function onVuePlyrInit () {
  const plyr = getPlyr()
  if (plyr == null) {
    throw new Error('Plyr instance is not available')
  }
  plyr.on('ready', () => {
    if (Platform.is.mobile === true) {
      initRangeTouch()
    }
  })
  emit('plyr-created')
}

const plyrOptions = computed<Plyr.Options>(() => ({
  fullscreen: {
    enabled: Platform.is.mobile,
    iosNative: false,
    fallback: false
  },
  clickToPlay: $behavior.doesntExist('preventClickToPlay') && Platform.is.desktop,
  vimeo: {
    controls: false,
    muted: muted.value,
    autoplay: autoplay.value,
    playsinline: true
  },
  youtube: {
    mute: muted.value,
    autoplay: autoplay.value,
    playsinline: 1
  },
  muted: muted.value,
  invertTime: false,
  controls: controls.value ?? '',
  storage: { enabled: false }
}))

defineExpose({
  getPlyr,
  getPlyrElement
})
</script>

<template>
  <div class="sd-ee-plyr-template">
    <VuePlyr
      v-if="mediaSource"
      ref="vuePlyrRef"
      :class="plyrClasses"
      :options="plyrOptions"
      @ready="onVuePlyrInit"
    >
      <video
        v-if="mediaSource.provider === 'html5' && mediaSource.url"
        ref="htmlVideoEl"
        :muted="muted"
        :autoplay="autoplay"
        :loop="loop"
        controls
        playsinline
        :poster="poster"
      >
        <source
          :src="mediaSource.url"
          type="video/mp4"
        >
      </video>
      <div
        v-else-if="mediaSource.provider === 'youtube'"
        class="fit plyr__video-embed"
      >
        <iframe
          :src="`https://www.youtube.com/embed/${mediaSource.id}?iv_load_policy=3&playsinline=1&modestbranding=1&showinfo=0&rel=0&enablejsapi=1&loop=${loop ? 1 : 0}`"
          allowfullscreen
          allowtransparency
          allow="autoplay"
        />
      </div>
      <div
        v-else-if="mediaSource.provider === 'vimeo'"
        class="fit plyr__video-embed"
      >
        <iframe
          style="height:500px"
          :src="`https://player.vimeo.com/video/${mediaSource.id}?byline=false&portrait=false&title=false&speed=true&transparent=0&gesture=media&loop=${loop ? 1 : 0}`"
          allowfullscreen
          allowtransparency
          allow="autoplay"
        />
      </div>
    </VuePlyr>
  </div>
</template>

<style scoped>
.fit {
  width: 100%;
  height: 100%;
}
</style>
